import { SupportWhatsAppLink } from "@/components/support/SupportWhatsAppLink";
import { useProfile } from "@/queries/customer/customer";
import { cn } from "@repo/design-system/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Bell,
  Boxes,
  Handshake,
  HelpCircle,
  ListChecks,
  Lock,
  MapPin,
  Settings,
  User
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { memo } from "react";

export interface NavigationItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  description: string;
}

export const navigationItems: NavigationItem[] = [
  {
    id: "profile",
    label: "Profile",
    icon: User,
    path: "/account",
    description: "Manage your personal information"
  },
  {
    id: "addresses",
    label: "Addresses",
    icon: MapPin,
    path: "/account/addresses",
    description: "Manage delivery and billing addresses"
  },
  {
    id: "notifications",
    label: "Notifications",
    icon: Bell,
    path: "/account/messages",
    description: "View notifications and messages"
  },
  {
    id: "supplier-match",
    label: "Supplier Match",
    icon: Handshake,
    path: "/account/supplier-match",
    description: "See which suppliers recognize your account"
  },
  {
    id: "password",
    label: "Password",
    icon: Lock,
    path: "/account/security",
    description: "Manage password and security settings"
  },
  {
    id: "preferences",
    label: "Preferences",
    icon: Settings,
    path: "/account/preferences",
    description: "Manage your preferences"
  }
];

/**
 * Account navigation sidebar component
 *
 * Features:
 * - Keyboard accessible (Tab, Enter, Arrow keys)
 * - Screen reader friendly with ARIA labels
 * - Visual active state indication
 * - Memoized for performance
 * - Help card at bottom
 */
export const AccountNavigation = memo(function AccountNavigation() {
  const { data } = useProfile();
  const router = useRouterState();
  const currentPath = router.location.pathname;

  /**
   * Determine if a navigation item is active
   * - Profile route is active only when path is exactly /account
   * - Other routes are active when path starts with their path
   */
  const isItemActive = (item: NavigationItem): boolean => {
    if (item.path === "/account") {
      return currentPath === "/account";
    }
    return currentPath.startsWith(item.path);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Navigation Links */}
      <nav aria-label="Account sections">
        <ul className="space-y-1" role="list">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = isItemActive(item);

            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    "focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2",
                    "hover:bg-gray-100 dark:hover:bg-neutral-800",
                    isActive && [
                      "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300"
                    ],
                    !isActive && "text-gray-700 dark:text-gray-300"
                  )}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={`${item.label}: ${item.description}`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Help Card */}
      <div className="mt-6 rounded-lg bg-purple-50 p-4 dark:bg-purple-900/20">
        <div className="flex items-start gap-2">
          <HelpCircle
            className="mt-0.5 h-4 w-4 flex-shrink-0 text-purple-600 dark:text-purple-400"
            aria-hidden="true"
          />
          <div>
            <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100">
              Need Help?
            </h3>
            <p className="mt-1 text-xs text-purple-700 dark:text-purple-300">
              Our support team is here to assist you with any account-related
              questions.
            </p>
            <SupportWhatsAppLink
              profile={data?.customer}
              className="mt-2 inline-flex items-center text-xs font-medium text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200"
            >
              Contact Support →
            </SupportWhatsAppLink>
          </div>
        </div>
      </div>
    </div>
  );
});
